import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Grid,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import Footer from "../../components/ui/Footer";
import {
  CART_STORAGE_KEY,
  addCnmapToCart,
  clearCnmapCart,
} from "../../helpers/openInNewTab";
import EmbeddedLoginForm from "./EmbeddedLoginForm";

const CNMAP_PRODUCT = {
  id: 57,
  productId: 52,
  name: "The Certified Nurse Medical Affairs Professional (CNMAP™) Program",
  nickname: "cnmap",
  type: "learningPlan",
  price: 649.0,
  tagLine: "Certification Program",
  image:
    "https://acma-media-bucket.s3.us-east-2.amazonaws.com/images/brand/acmaMarketplace/productImages/cnmap.svg",
  excerpt:
    "The Certified Nurse Medical Affairs Program (CNMAP™) is a self-paced, online, certification program that provides nurses and advanced degree nurses the opportunity to educate themselves in the areas of medical affairs and life sciences, and therefore, creating a new avenue to career development. The CNMAP™ program enhances understanding of the medical affairs landscape within the pharmaceutical industry, allowing nursing professionals to power and advance their career in this rapidly expanding field.",
  details: [
    "15 Modules",
    "6 Months Access",
    "100% Online & Self Paced",
    "Earn CME/CE Credits",
  ],
};

const AUTH_STORAGE_KEY = "cnmap_checkout_user";
const CHECKOUT_FORM_KEY = "cnmap_checkout_form";
const AUTO_RESUME_KEY = "cnmap_checkout_auto_resume";

const DEFAULT_DISCOUNTED = {
  success: false,
  savings: 0,
  discountedTotal: CNMAP_PRODUCT.price,
  couponName: "",
  precheckUnavailable: false,
};

function readCheckoutFormState() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(CHECKOUT_FORM_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveCheckoutFormState(state) {
  if (typeof window === "undefined") return;
  try {
    const existing = readCheckoutFormState() || {};
    window.sessionStorage.setItem(
      CHECKOUT_FORM_KEY,
      JSON.stringify({ ...existing, ...state })
    );
  } catch {
    // ignore storage errors
  }
}

function clearCheckoutFormState() {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(CHECKOUT_FORM_KEY);
  } catch {
    // ignore storage errors
  }
}

function readHasCartItems() {
  if (typeof window === "undefined") return true;
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) {
      addCnmapToCart();
      return true;
    }
    const parsed = JSON.parse(raw);
    const products =
      parsed && parsed.state && Array.isArray(parsed.state.products)
        ? parsed.state.products
        : Array.isArray(parsed)
          ? parsed
          : null;
    if (Array.isArray(products)) {
      return products.length > 0;
    }
    return true;
  } catch {
    return true;
  }
}

function decodeSsoPayload(encoded) {
  try {
    const b64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
    const padded =
      b64.length % 4 === 0 ? b64 : b64 + "=".repeat(4 - (b64.length % 4));
    const json = decodeURIComponent(
      atob(padded)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function getUTMParams() {
  return {
    utm_source: localStorage.getItem("utm_source") || null,
    utm_medium: localStorage.getItem("utm_medium") || null,
    utm_campaign: localStorage.getItem("utm_campaign") || null,
  };
}

export default function CheckoutPage() {
  const theme = useTheme();
  const [hasCartItems, setHasCartItems] = useState(() => readHasCartItems());
  const [authUser, setAuthUser] = useState(() => {
    try {
      const saved = sessionStorage.getItem(AUTH_STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoInput, setPromoInput] = useState(() => {
    const saved = readCheckoutFormState();
    return (saved && (saved.promoInput || saved.appliedPromoCode)) || "";
  });
  const [appliedPromoCode, setAppliedPromoCode] = useState(() => {
    const saved = readCheckoutFormState();
    return (saved && saved.appliedPromoCode) || "";
  });
  const [promoLoading, setPromoLoading] = useState(false);
  const [discounted, setDiscounted] = useState(() => {
    const saved = readCheckoutFormState();
    if (saved && saved.discounted && typeof saved.discounted === "object") {
      return { ...DEFAULT_DISCOUNTED, ...saved.discounted };
    }
    return DEFAULT_DISCOUNTED;
  });
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    window.scrollTo(0, 0);

    const savedForm = readCheckoutFormState();
    if (savedForm && savedForm.appliedPromoCode) {
      setPromoInput(savedForm.promoInput || savedForm.appliedPromoCode);
      setAppliedPromoCode(savedForm.appliedPromoCode);
      if (savedForm.discounted && typeof savedForm.discounted === "object") {
        setDiscounted({ ...DEFAULT_DISCOUNTED, ...savedForm.discounted });
      }
    }

    const params = new URLSearchParams(window.location.search);
    const authError = params.get("auth_error");
    if (authError) {
      setStatusMessage({
        type: "error",
        text:
          authError === "access_denied"
            ? "Login was cancelled or denied. Please try again."
            : "Login could not be completed. Please try again.",
      });
      window.history.replaceState({}, "", window.location.pathname);
    }

    const ssoMatch = (window.location.hash || "").match(/#sso=([A-Za-z0-9_-]+)/);
    if (!ssoMatch) return;

    const ssoUser = decodeSsoPayload(ssoMatch[1]);
    window.history.replaceState(
      {},
      "",
      window.location.pathname + window.location.search
    );
    if (!ssoUser || !ssoUser.sub || !ssoUser.email) return;

    try {
      sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(ssoUser));
    } catch {
      // ignore storage errors
    }
    setAuthUser(ssoUser);
    setShowLoginForm(false);

    const restoredPromo =
      (savedForm && savedForm.appliedPromoCode) || appliedPromoCode || "";

    let shouldResume = false;
    try {
      shouldResume = sessionStorage.getItem(AUTO_RESUME_KEY) === "1";
      sessionStorage.removeItem(AUTO_RESUME_KEY);
    } catch {
      // ignore
    }
    if (shouldResume) {
      runCheckout(ssoUser, restoredPromo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const syncCart = () => setHasCartItems(readHasCartItems());
    window.addEventListener("cnmap-cart-updated", syncCart);
    window.addEventListener("storage", syncCart);
    return () => {
      window.removeEventListener("cnmap-cart-updated", syncCart);
      window.removeEventListener("storage", syncCart);
    };
  }, []);

  const handleRemoveCartItem = () => {
    clearCnmapCart();
    setHasCartItems(false);
    setPromoInput("");
    setAppliedPromoCode("");
    setDiscounted(DEFAULT_DISCOUNTED);
    clearCheckoutFormState();
    setStatusMessage({ type: "", text: "" });
  };

  const totalPrice = discounted.success
    ? discounted.discountedTotal
    : CNMAP_PRODUCT.price;

  const handleApplyPromoCode = async () => {
    const code = promoInput.trim();
    if (!code) return;

    setPromoLoading(true);
    setStatusMessage({ type: "", text: "" });

    try {
      const res = await fetch(
        `/api/promo-code?couponCode=${encodeURIComponent(
          code
        )}&productIds=${encodeURIComponent(
          JSON.stringify([CNMAP_PRODUCT.productId])
        )}`
      );
      const data = await res.json().catch(() => null);

      if (!res.ok || !data || !data.success) {
        if (data && data.precheckUnavailable) {
          const fallbackDiscounted = {
            success: false,
            savings: 0,
            discountedTotal: CNMAP_PRODUCT.price,
            couponName: code,
            precheckUnavailable: true,
          };
          setPromoInput(code);
          setAppliedPromoCode(code);
          setDiscounted(fallbackDiscounted);
          saveCheckoutFormState({
            promoInput: code,
            appliedPromoCode: code,
            discounted: fallbackDiscounted,
          });
          setPromoOpen(false);
          setStatusMessage({
            type: "info",
            text:
              data.message ||
              "Promo pre-check is temporarily unavailable. We will validate this code at checkout.",
          });
          return;
        }

        setAppliedPromoCode("");
        setDiscounted(DEFAULT_DISCOUNTED);
        clearCheckoutFormState();
        setStatusMessage({
          type: "error",
          text: (data && data.message) || "Invalid Coupon Code",
        });
        return;
      }

      const nextDiscounted = {
        success: true,
        savings: Number(data.savings || 0),
        discountedTotal: Number(
          data.discountedTotal != null
            ? data.discountedTotal
            : CNMAP_PRODUCT.price
        ),
        couponName: data.couponName || code,
        precheckUnavailable: false,
      };
      setPromoInput(code);
      setAppliedPromoCode(code);
      setDiscounted(nextDiscounted);
      saveCheckoutFormState({
        promoInput: code,
        appliedPromoCode: code,
        discounted: nextDiscounted,
      });
      setPromoOpen(false);
      setStatusMessage({
        type: "success",
        text: `Promo code "${code}" applied (-$${Number(
          data.savings || 0
        ).toFixed(2)})`,
      });
    } catch {
      setStatusMessage({
        type: "error",
        text: "Unable to validate promo code right now. Please try again.",
      });
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromoCode = () => {
    setPromoInput("");
    setAppliedPromoCode("");
    setDiscounted(DEFAULT_DISCOUNTED);
    clearCheckoutFormState();
    setStatusMessage({ type: "", text: "" });
  };

  const runCheckout = async (userObj, promoCodeOverride) => {
    const activeUser = userObj || authUser;
    if (!activeUser || !activeUser.email || !activeUser.sub) {
      setShowLoginForm(true);
      return;
    }
    const effectivePromo =
      promoCodeOverride !== undefined ? promoCodeOverride : appliedPromoCode;

    setCheckoutLoading(true);
    setStatusMessage({ type: "", text: "" });

    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(activeUser.accessToken
            ? { Authorization: `Bearer ${activeUser.accessToken}` }
            : {}),
        },
        body: JSON.stringify({
          cartProductsIds: [CNMAP_PRODUCT.productId],
          items: [
            {
              productId: CNMAP_PRODUCT.productId,
              id: CNMAP_PRODUCT.id,
              name: CNMAP_PRODUCT.name,
              nickname: CNMAP_PRODUCT.nickname,
              type: CNMAP_PRODUCT.type,
              price: CNMAP_PRODUCT.price,
              quantity: 1,
            },
          ],
          paymentType: "fullPayment",
          userEmail: activeUser.email,
          userId: activeUser.sub,
          promoCode: effectivePromo || undefined,
          position: { id: 7, title: "USA", nickName: "US" },
          params: getUTMParams(),
          accessToken: activeUser.accessToken || undefined,
        }),
      });

      const data = await response.json().catch(() => null);
      const redirectUrl =
        data &&
        (typeof data.data === "string"
          ? data.data
          : data.data && data.data.url
            ? data.data.url
            : data.url);

      if (!response.ok || !data || data.success === false || !redirectUrl) {
        setStatusMessage({
          type: "error",
          text:
            (data && data.message) ||
            "Error getting payment URL. Please try again.",
        });
        setCheckoutLoading(false);
        return;
      }

      window.location.href = redirectUrl;
    } catch {
      setStatusMessage({
        type: "error",
        text: "Network error starting checkout. Please try again.",
      });
      setCheckoutLoading(false);
    }
  };

  const handleLoginSuccess = async (loggedInUser) => {
    try {
      sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(loggedInUser));
    } catch {
      // ignore storage errors
    }
    setAuthUser(loggedInUser);
    setShowLoginForm(false);
    await runCheckout(loggedInUser);
  };

  const handleSocialRedirect = (connection) => {
    saveCheckoutFormState({ promoInput, appliedPromoCode, discounted });
    try {
      sessionStorage.setItem(AUTO_RESUME_KEY, "1");
    } catch {
      // ignore
    }
    const params = new URLSearchParams({
      connection,
      returnTo: "/checkout",
    });
    const utm = getUTMParams();
    if (utm.utm_source) params.set("utm_source", utm.utm_source);
    if (utm.utm_medium) params.set("utm_medium", utm.utm_medium);
    if (utm.utm_campaign) params.set("utm_campaign", utm.utm_campaign);
    window.location.href = `/api/auth/login?${params.toString()}`;
  };

  const handleLogout = () => {
    try {
      sessionStorage.removeItem(AUTH_STORAGE_KEY);
    } catch {
      // ignore
    }
    setAuthUser(null);
    setShowLoginForm(false);
  };

  return (
    <>
      <Helmet>
        <title>Shopping Cart & Checkout | CNMAP™</title>
        <meta
          name="description"
          content="Complete your enrollment in The Certified Nurse Medical Affairs Professional (CNMAP™) Program."
        />
      </Helmet>

      <Box
        sx={{
          backgroundColor: "#f8fafc",
          minHeight: "calc(100vh - 200px)",
          py: { xs: 4, md: 7 },
        }}
      >
        {!hasCartItems ? (
          <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 } }}>
            <Box sx={{ textAlign: "center", maxWidth: 640, mx: "auto" }}>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  color: "#1f2937",
                  mb: 2,
                }}
              >
                Your cart is Empty
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "#4b5563",
                  fontWeight: 400,
                  mb: 4,
                }}
              >
                It seems your cart is currently empty
              </Typography>
              <Button
                component={RouterLink}
                to="/home"
                variant="contained"
                size="large"
                sx={{
                  px: 5,
                  py: 1.75,
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  borderRadius: "10px",
                }}
              >
                Learn about CNMAP
              </Button>
            </Box>
          </Container>
        ) : (
        <Container maxWidth="lg">
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
            <Link
              component={RouterLink}
              to="/home"
              underline="hover"
              color="inherit"
               sx={{ fontSize: "0.875rem" }}
            >
              Home
            </Link>
            <Link
              component={RouterLink}
              to="/cnmap-payment"
              underline="hover"
              color="inherit"
              sx={{ fontSize: "0.875rem" }}
            >
              Payment Options
            </Link>
            <Typography color="text.primary" sx={{ fontSize: "0.875rem" }}>
              Checkout
            </Typography>
          </Breadcrumbs>

          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: theme.palette.primary.main,
              mb: 4,
            }}
          >
            Shopping Cart
          </Typography>

          <Grid container spacing={4} alignItems="flex-start">
            {/* Left column: Cart Product Card + ACMA Accreditation stats */}
            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  borderRadius: "16px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
                  overflow: "hidden",
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: -1,
                      mr: -1,
                      mb: 0.5,
                    }}
                  >
                    <IconButton
                      aria-label="delete"
                      size="small"
                      onClick={handleRemoveCartItem}
                      sx={{ color: "#0f172a" }}
                    >
                      <HighlightOffOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm={4}>
                      <Box
                        sx={{
                          position: "relative",
                          borderRadius: "12px",
                          overflow: "hidden",
                          backgroundColor: "#032d3a",
                          p: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minHeight: 165,
                        }}
                      >
                        <Box
                          component="img"
                          src={CNMAP_PRODUCT.image}
                          alt={CNMAP_PRODUCT.name}
                          sx={{
                            maxWidth: "100%",
                            maxHeight: 140,
                            objectFit: "contain",
                          }}
                        />
                        <Chip
                          label={CNMAP_PRODUCT.tagLine}
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            backgroundColor: "#ffc81a",
                            color: "#000",
                            fontWeight: 700,
                            fontSize: "0.7rem",
                          }}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={8}>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: "#1e293b",
                          mb: 1,
                        }}
                      >
                        {CNMAP_PRODUCT.name}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ color: "#475569", mb: 2, lineHeight: 1.6 }}
                      >
                        {CNMAP_PRODUCT.excerpt}
                      </Typography>

                      <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        flexWrap="wrap"
                        sx={{ mb: 2 }}
                      >
                        {CNMAP_PRODUCT.details.map((detail, index) => (
                          <Typography
                            key={detail}
                            variant="body2"
                            sx={{ color: "#64748b" }}
                          >
                            {index > 0 ? "• " : ""}
                            {detail}
                          </Typography>
                        ))}
                      </Stack>

                      <Divider sx={{ my: 1.5 }} />

                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        alignItems="center"
                      >
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 800,
                            color: theme.palette.primary.main,
                          }}
                        >
                          ${CNMAP_PRODUCT.price.toFixed(2)}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Accreditation & Recognition Stats (matching PACS/MAS SingleCartProductCard) */}
              <Card
                sx={{
                  mt: 3,
                  borderRadius: "16px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "none",
                  backgroundColor: "#fff",
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                  <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                        }}
                      >
                        Accredited
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#475569", mt: 0.5 }}
                      >
                        All of the ACMA&apos;s programs are accredited by
                        IACET/ANSI & ANCC
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                        }}
                      >
                        1000+
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#475569", mt: 0.5 }}
                      >
                        pharma companies recognize & acknowledge ACMA
                        certifications
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          color: theme.palette.primary.main,
                        }}
                      >
                        8,000+
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#475569", mt: 0.5 }}
                      >
                        professionals certified across the life sciences
                        industry
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Right column: TotalCard (Promo code, summary, EmbeddedLoginForm, Checkout button) */}
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  borderRadius: "16px",
                  border: "1px solid #cbd5e1",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: "#0f172a", mb: 1 }}
                  >
                    Cart Total
                  </Typography>

                  {authUser ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        backgroundColor: "#f1f5f9",
                        borderRadius: "8px",
                        px: 1.5,
                        py: 1,
                        mb: 2,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ minWidth: 0 }}
                      >
                        <PersonOutlineIcon
                          fontSize="small"
                          sx={{ color: theme.palette.primary.main }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "#1e293b",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {authUser.email}
                        </Typography>
                      </Stack>
                      <Link
                        component="button"
                        type="button"
                        variant="caption"
                        onClick={handleLogout}
                        sx={{ ml: 1, textDecoration: "underline" }}
                      >
                        Switch
                      </Link>
                    </Box>
                  ) : null}

                  {/* Promo Code Toggle & Input */}
                  <Box sx={{ mb: 2 }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => setPromoOpen((prev) => !prev)}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        color: "#334155",
                        fontWeight: 600,
                        textDecoration: "underline",
                      }}
                    >
                      <LocalOfferOutlinedIcon sx={{ fontSize: "1rem" }} />
                      Apply Promo Code
                    </Link>

                    <Collapse in={promoOpen}>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ mt: 1.5 }}
                        alignItems="center"
                      >
                        <TextField
                          size="small"
                          placeholder="Enter promo code"
                          value={promoInput}
                          disabled={Boolean(appliedPromoCode) || promoLoading}
                          onChange={(e) => setPromoInput(e.target.value)}
                          fullWidth
                        />
                        <Button
                          variant="contained"
                          onClick={handleApplyPromoCode}
                          disabled={
                            !promoInput.trim() ||
                            Boolean(appliedPromoCode) ||
                            promoLoading
                          }
                          sx={{ minWidth: 84 }}
                        >
                          {promoLoading ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            "Apply"
                          )}
                        </Button>
                      </Stack>
                    </Collapse>

                    {appliedPromoCode ? (
                      <Box
                        sx={{
                          mt: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          backgroundColor: discounted.precheckUnavailable
                            ? "#fffbeb"
                            : "#ecfdf5",
                          color: discounted.precheckUnavailable
                            ? "#92400e"
                            : "#047857",
                          border: `1px solid ${
                            discounted.precheckUnavailable
                              ? "#fde68a"
                              : "#a7f3d0"
                          }`,
                          borderRadius: "8px",
                          px: 1.5,
                          py: 0.75,
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>
                          {discounted.precheckUnavailable
                            ? `Code queued for checkout: ${appliedPromoCode}`
                            : `Promo code applied: ${appliedPromoCode}`}
                        </Typography>
                        <Link
                          component="button"
                          type="button"
                          variant="caption"
                          onClick={handleRemovePromoCode}
                          sx={{
                            color: "inherit",
                            fontWeight: 700,
                            textDecoration: "underline",
                          }}
                        >
                          Remove
                        </Link>
                      </Box>
                    ) : null}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Line Summary */}
                  <Stack spacing={1.25}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ color: "#334155" }}>
                        Items
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: "#0f172a" }}
                      >
                        x1
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ color: "#334155" }}>
                        Program Price
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#0f172a" }}>
                        ${CNMAP_PRODUCT.price.toFixed(2)}
                      </Typography>
                    </Stack>

                    {discounted.success ? (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography
                          variant="body2"
                          sx={{ color: "#047857", fontWeight: 600 }}
                        >
                          Discount ({discounted.couponName})
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#047857", fontWeight: 600 }}
                        >
                          -${discounted.savings.toFixed(2)}
                        </Typography>
                      </Stack>
                    ) : null}

                    <Divider />

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, color: "#0f172a" }}
                      >
                        Subtotal
                      </Typography>
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 800,
                          color: theme.palette.primary.main,
                        }}
                      >
                        ${totalPrice.toFixed(2)}
                      </Typography>
                    </Stack>
                  </Stack>

                  {statusMessage.text ? (
                    <Alert
                      severity={statusMessage.type || "info"}
                      sx={{ mt: 2, fontSize: "0.8rem" }}
                    >
                      {statusMessage.text}
                    </Alert>
                  ) : null}

                  {/* Inline EmbeddedLoginForm or Checkout Button */}
                  {showLoginForm && !authUser ? (
                    <Box sx={{ mt: 2.5 }}>
                      <EmbeddedLoginForm
                        initialEmail=""
                        onLoginStart={() =>
                          setStatusMessage({ type: "", text: "" })
                        }
                        onLoginSuccess={handleLoginSuccess}
                        onLoginError={(msg) => {
                          if (msg) {
                            setStatusMessage({ type: "error", text: msg });
                          }
                        }}
                        onSocialRedirect={handleSocialRedirect}
                        onCancel={() => {
                          setShowLoginForm(false);
                          setCheckoutLoading(false);
                        }}
                      />
                    </Box>
                  ) : (
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={checkoutLoading}
                      onClick={() => {
                        if (!authUser) {
                          setShowLoginForm(true);
                          return;
                        }
                        runCheckout(authUser);
                      }}
                      endIcon={
                        !checkoutLoading ? <ChevronRightIcon /> : undefined
                      }
                      sx={{
                        mt: 3,
                        py: 1.5,
                        fontSize: "1rem",
                        fontWeight: 700,
                        borderRadius: "10px",
                      }}
                    >
                      {checkoutLoading ? (
                        <>
                          <CircularProgress
                            size={18}
                            color="inherit"
                            sx={{ mr: 1 }}
                          />
                          Redirecting to Stripe...
                        </>
                      ) : (
                        "Checkout"
                      )}
                    </Button>
                  )}

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                    sx={{ mt: 2, color: "#64748b" }}
                  >
                    <VerifiedUserOutlinedIcon sx={{ fontSize: "1rem" }} />
                    <Typography variant="caption">
                      256-bit SSL Secure Stripe Checkout
                    </Typography>
                  </Stack>

                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "#64748b",
                      textAlign: "center",
                      mt: 1.5,
                      lineHeight: 1.4,
                    }}
                  >
                    I understand and agree that this is a non-refundable digital
                    product, that access constitutes delivery, and that
                    chargebacks after accessing content may be disputed as
                    fraud. No refunds, exchanges, chargebacks or credits will be
                    provided under any circumstances. You also agree to our
                    terms and conditions and privacy policy.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
        )}
      </Box>

      <Footer />
    </>
  );
}
